import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  HStack,
  Input as DesignSystemInput,
  Textarea,
  Image,
} from '@skin-seoul/design-system';
import {
  COLORS,
  FONT_FAMILIES,
  FONT_SIZES,
  FONT_WEIGHTS,
  BORDER_RADIUS,
  SPACING,
  Z_INDEX,
  TRANSITIONS,
} from './constants';

export const Section = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <VStack align="start" gap={SPACING.md} style={style}>
    {children}
  </VStack>
);

export const Row = ({ children }: { children: React.ReactNode }) => <HStack gap={SPACING.sm}>{children}</HStack>;

export const Divider = ({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) => (
  <Box width="100%" height="1px" bg={COLORS.border} flexShrink="0" style={style}>
    {children}
  </Box>
);

export const Label = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      fontSize: FONT_SIZES.base,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.text,
      fontFamily: FONT_FAMILIES.primary,
      lineHeight: '140%',
    }}
  >
    {children}
  </label>
);

export const TextArea = ({
  id,
  maxLength,
  value,
  onChange,
}: {
  id?: string;
  maxLength?: number;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) => (
  <Textarea
    id={id}
    maxLength={maxLength}
    value={value}
    onChange={onChange}
    border={`1px solid ${COLORS.border}`}
    borderRadius={BORDER_RADIUS.sm}
    p={`${SPACING.md} ${SPACING.sm}`}
    style={{ fontSize: FONT_SIZES.md }}
    minH="120px"
    resize="vertical"
    outline="none"
    fontFamily={FONT_FAMILIES.primary}
    css={{
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
        borderRadius: BORDER_RADIUS.sm,
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: BORDER_RADIUS.sm,
        border: '2px solid transparent',
        backgroundClip: 'content-box',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#a8a8a8',
        backgroundClip: 'content-box',
      },
      scrollbarWidth: 'medium',
      scrollbarColor: '#c1c1c1 transparent',
    }}
  />
);

export const FileInput = ({
  id,
  type,
  accept,
  multiple,
  onChange,
}: {
  id?: string;
  type: string;
  accept?: string;
  multiple?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => <input id={id} type={type} accept={accept} multiple={multiple} onChange={onChange} style={{ display: 'none' }} />;

export const StarRow = ({ children }: { children: React.ReactNode }) => <HStack gap={SPACING.xs}>{children}</HStack>;

export const Star = ({
  children,
  selected: _selected,
  onClick,
  ...props
}: {
  children: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
}) => (
  <Button
    bg="none"
    border="none"
    p="0"
    cursor="pointer"
    display="flex"
    alignItems="center"
    justifyContent="center"
    onClick={onClick}
    {...props}
    minW="40px"
    h="40px"
    css={{
      '& svg': {
        width: '40px',
        height: '40px',
      },
    }}
  >
    {children}
  </Button>
);

export const ImageUploadBox = ({ children }: { children: React.ReactNode }) => (
  <Flex direction="row" align="center" gap={SPACING.sm} w="full" mt={SPACING.xs} flexWrap="wrap">
    {children}
  </Flex>
);

export const Thumbnail = ({ children }: { children: React.ReactNode }) => (
  <Box
    position="relative"
    w="100px"
    h="100px"
    borderRadius={BORDER_RADIUS.sm}
    overflow="visible"
    border="1px solid #eee"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    {children}
  </Box>
);

export const ThumbnailImage = ({ src, alt }: { src: string; alt: string }) => (
  <Image src={src} alt={alt} w="full" h="full" objectFit="cover" borderRadius={BORDER_RADIUS.sm} />
);

export const RemoveButton = ({
  children,
  onClick,
  ...props
}: {
  children: React.ReactNode;
  onClick: () => void;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
}) => (
  <Button
    position="absolute"
    top="4px"
    right="2px"
    bg={COLORS.backgroundWithBlur}
    borderRadius={BORDER_RADIUS.full}
    w="20px"
    h="20px"
    maxW="20px"
    minW={0}
    cursor="pointer"
    display="flex"
    alignItems="center"
    justifyContent="center"
    p="2px"
    zIndex="2"
    onClick={onClick}
    {...props}
  >
    {children}
  </Button>
);

export const UploadArea = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      border: `1px dashed ${COLORS.borderDashed}`,
      borderRadius: BORDER_RADIUS.sm,
      width: '100%',
      height: '100px',
      background: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: TRANSITIONS.border,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = COLORS.borderHover)}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = COLORS.borderDashed)}
  >
    {children}
  </label>
);

export const SmallUploadArea = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => (
  <label
    htmlFor={htmlFor}
    style={{
      border: `1px dashed ${COLORS.borderDashed}`,
      borderRadius: BORDER_RADIUS.sm,
      width: '100px',
      height: '100px',
      minWidth: '100px',
      minHeight: '100px',
      background: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: TRANSITIONS.border,
      marginTop: '0',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = COLORS.borderHover)}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = COLORS.borderDashed)}
  >
    {children}
  </label>
);

export const RadioGroup = ({ children }: { children: React.ReactNode }) => (
  <VStack align="start" gap={SPACING.lg} mt={SPACING.sm}>
    {children}
  </VStack>
);

export const RadioLabelWrapper = ({ children }: { children: React.ReactNode }) => (
  <VStack align="start" gap={SPACING.sm}>
    {children}
  </VStack>
);

export const RadioLabel = ({ children }: { children: React.ReactNode }) => (
  <Flex
    align="center"
    gap={SPACING.sm}
    cursor="pointer"
    as="label"
    style={{
      fontSize: FONT_SIZES.md,
      color: COLORS.text,
      fontFamily: FONT_FAMILIES.primary,
    }}
  >
    {children}
  </Flex>
);

export const Radio = ({
  type,
  name,
  value,
  checked,
  onChange,
}: {
  type: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <input
    type={type}
    name={name}
    value={value}
    checked={checked}
    onChange={onChange}
    style={{
      accentColor: COLORS.primary,
      width: '18px',
      height: '18px',
    }}
  />
);

export const SubmitButton = ({
  children,
  disabled,
  onClick,
  type,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  type?: 'button' | 'submit' | 'reset';
}) => (
  <Button
    type={type}
    disabled={disabled}
    onClick={onClick}
    bg={disabled ? COLORS.primaryDisabled : COLORS.primary}
    color={COLORS.white}
    border="none"
    borderRadius={BORDER_RADIUS.sm}
    style={{ fontSize: FONT_SIZES.sm }}
    fontWeight={FONT_WEIGHTS.normal}
    h="48px"
    w="full"
    cursor={disabled ? 'not-allowed' : 'pointer'}
    opacity="1"
    boxShadow="none"
    display="flex"
    alignItems="center"
    justifyContent="center"
    transition={TRANSITIONS.background}
    fontFamily={FONT_FAMILIES.primary}
    css={{
      '&:hover:not(:disabled)': {
        opacity: 0.9,
      },
    }}
  >
    {children}
  </Button>
);

export const CompleteButton = ({
  children,
  onClick,
  style,
}: {
  children: React.ReactNode;
  onClick: () => void;
  style?: React.CSSProperties;
}) => (
  <Button
    onClick={onClick}
    bg={COLORS.primary}
    color={COLORS.white}
    border="none"
    borderRadius={BORDER_RADIUS.sm}
    style={{ fontSize: FONT_SIZES.base, ...style }}
    fontWeight={FONT_WEIGHTS.semibold}
    fontFamily={FONT_FAMILIES.primary}
    cursor="pointer"
    boxShadow="none"
    w="full"
    _hover={{ opacity: 0.9 }}
  >
    {children}
  </Button>
);

export const GuideText = ({ children }: { children: React.ReactNode }) => (
  <Text style={{ fontSize: FONT_SIZES.xs }} color={COLORS.textSecondary} m="0" fontFamily={FONT_FAMILIES.primary}>
    {children}
  </Text>
);

export const Optional = ({ children }: { children: React.ReactNode }) => (
  <Text
    color={COLORS.borderDashed}
    fontFamily={FONT_FAMILIES.primary}
    style={{
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
    }}
    fontWeight={FONT_WEIGHTS.normal}
  >
    {children}
  </Text>
);

export const LabelWrapper = ({ children }: { children: React.ReactNode }) => (
  <HStack align="center" gap={SPACING.sm}>
    {children}
  </HStack>
);

export const BottomButtonRow = ({ children }: { children: React.ReactNode }) => <Box>{children}</Box>;

export const TermsBox = ({ children }: { children: React.ReactNode }) => (
  <VStack align="start" gap={SPACING.md}>
    {children}
  </VStack>
);

export const TermsTitle = ({ children }: { children: React.ReactNode }) => (
  <Text
    color={COLORS.primary}
    fontFamily={FONT_FAMILIES.primary}
    style={{
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
    }}
    fontWeight={FONT_WEIGHTS.normal}
  >
    {children}
  </Text>
);

export const TermsText = ({ children }: { children: React.ReactNode }) => (
  <Text
    color={COLORS.textSecondary}
    fontFamily={FONT_FAMILIES.primary}
    style={{
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
    }}
    fontWeight={FONT_WEIGHTS.normal}
  >
    {children}
  </Text>
);

export const IntroSection = ({ children }: { children: React.ReactNode }) => (
  <Flex justify="space-between" align="center" gap={SPACING.lg} minH="136px">
    {children}
  </Flex>
);

export const TextContainer = ({ children }: { children: React.ReactNode }) => (
  <VStack align="start" gap={SPACING.sm} flex="1" justify="center">
    {children}
  </VStack>
);

export const IntroText = ({ children }: { children: React.ReactNode }) => (
  <Text
    color={COLORS.text}
    fontFamily={FONT_FAMILIES.primary}
    style={{
      fontSize: FONT_SIZES.sm,
      fontStyle: 'normal',
      lineHeight: '140%',
    }}
    fontWeight={FONT_WEIGHTS.normal}
  >
    {children}
  </Text>
);

export const IntroSubText = ({ children }: { children: React.ReactNode }) => (
  <Text
    color={COLORS.textSecondary}
    fontFamily={FONT_FAMILIES.primary}
    style={{
      fontSize: FONT_SIZES.xs,
      fontFeatureSettings: "'ss10' on",
      fontStyle: 'normal',
      lineHeight: '140%',
    }}
    fontWeight={FONT_WEIGHTS.normal}
  >
    {children}
  </Text>
);

export const ImageContainer = ({ children }: { children: React.ReactNode }) => (
  <Box
    w="136px"
    h="136px"
    bg={COLORS.backgroundSecondary}
    borderRadius={SPACING.lg}
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink="0"
    overflow="hidden"
  >
    {children}
  </Box>
);

export const CompleteContainer = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <VStack align="stretch" gap={SPACING.lg} style={style}>
    {children}
  </VStack>
);

export const CompleteTitle = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Text
    color={COLORS.text}
    fontFamily={FONT_FAMILIES.primary}
    fontWeight={FONT_WEIGHTS.semibold}
    style={{ lineHeight: '140%', ...style }}
  >
    {children}
  </Text>
);

export const CompleteDesc = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Text
    color={COLORS.textSecondary}
    fontFamily={FONT_FAMILIES.primary}
    fontWeight={FONT_WEIGHTS.normal}
    style={{ lineHeight: '140%', ...style }}
  >
    {children}
  </Text>
);

export const ConfirmationOverlay = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  initial?: { opacity: number };
  animate?: { opacity: number };
  exit?: { opacity: number };
  onClick?: () => void;
}) => (
  <Box
    as="div"
    position="fixed"
    top="0"
    left="0"
    right="0"
    bottom="0"
    bg={COLORS.backgroundOverlayDark}
    zIndex={Z_INDEX.confirmation}
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  >
    {children}
  </Box>
);

export const RequiredIndicator = ({ children }: { children: React.ReactNode }) => (
  <Text as="span" color={COLORS.text}>
    {children}
  </Text>
);

export const Input = ({
  id,
  maxLength,
  value,
  onChange,
  ...props
}: {
  id?: string;
  maxLength?: number;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  border?: string;
  borderRadius?: string;
  p?: string;
  style?: React.CSSProperties;
  outline?: string;
  fontFamily?: string;
}) => (
  <DesignSystemInput
    id={id}
    maxLength={maxLength}
    value={value}
    onChange={onChange}
    border={`1px solid ${COLORS.border}`}
    borderRadius={BORDER_RADIUS.sm}
    p={`${SPACING.md} ${SPACING.sm}`}
    style={{ fontSize: FONT_SIZES.md }}
    outline="none"
    fontFamily={FONT_FAMILIES.primary}
    {...props}
  />
);
